import { useState, useCallback } from 'react';
import { get, post, put, del } from '../../utils/networkUtils';

export const usePickems = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all pickems with pagination
  const fetchPickems = useCallback(async (offset = 0, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/pickems?offset=${offset}&limit=${limit}`);
      return response.pickems;
    } catch (err) {
      setError(err.message || 'Failed to fetch pickems');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get single pickem by ID
  const fetchPickemById = useCallback(async (pickemId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/pickems/${pickemId}`);
      return response.pickem;
    } catch (err) {
      setError(err.message || 'Failed to fetch pickem');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new pickem
  const createPickem = useCallback(async (pickemData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post('/admin/pickems', pickemData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to create pickem');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update pickem
  const updatePickem = useCallback(async (pickemId, pickemData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await put(`/admin/pickems/${pickemId}`, pickemData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update pickem');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete pickem
  const deletePickem = useCallback(async (pickemId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post(`/admin/game/${pickemId}/delete`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to delete pickem');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Duplicate pickem
  const duplicatePickem = useCallback(async (pickemId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post(`/admin/pickems/${pickemId}/duplicate`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to duplicate pickem');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update pinned users
  const updatePinnedUsers = useCallback(async (pickemId, pinUserIds) => {
    setLoading(true);
    setError(null);
    try {
      const response = await put(`/admin/pickem-leaderboard/${pickemId}`, { pinUserIds });
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update pinned users');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user selections for a pickem
  const fetchUserSelections = useCallback(async (pickemId, userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/pickems/${pickemId}/user-selections/${userId}`);
      return response.selections;
    } catch (err) {
      setError(err.message || 'Failed to fetch user selections');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete pickem question
  const deletePickemQuestion = useCallback(async (pickemId, questionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await del(`/admin/pickem-questions/${pickemId}?question_id=${questionId}`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to delete question');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update pickem question
  const updatePickemQuestion = useCallback(async (pickemId, questionId, questionData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await put(`/admin/pickem-questions/${pickemId}?question_id=${questionId}`, questionData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update question');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchPickems,
    fetchPickemById,
    createPickem,
    updatePickem,
    deletePickem,
    duplicatePickem,
    updatePinnedUsers,
    fetchUserSelections,
    deletePickemQuestion,
    updatePickemQuestion,
  };
};
